<template>
  <div
    class="mbl-card"
    :class="[{ 'mbl-card--week': variant === 'week' }, timelineEventIdClass]"
    data-cy="mobile-appointment-card"
  >
    <template v-if="markerKind === 'appointment'">
      <!-- The work segment renders FIRST in document order regardless of its
           own geometry: the lead segments (pre-work, travel, late) get a
           higher z-index below, so the chevron a dispatcher reads the trip
           from is never painted over by the work block it now routinely
           overlaps (D-03 as amended). -->
      <div
        v-for="segment in orderedSegments"
        :key="segment.kind"
        class="mbl-card-segment"
        :class="[
          `mbl-card-segment--${segment.kind}`,
          segment.kind === 'work' ? 'mbl-card-work' : '',
          {
            'mbl-card-segment--rounded-top': segment.first,
            'mbl-card-segment--rounded-bottom': segment.last
          }
        ]"
        :data-segment="segment.kind"
        :data-cy="
          segment.kind !== 'work' ? 'mobile-card-segment' : 'mobile-card-work'
        "
        :style="segmentStyle(segment)"
      ></div>
      <!-- The content block hangs off the CARD, not the work segment: the
           ladder now measures the whole appointment, so an hour card shows
           both lines whatever the travel stripe leaves the work block. It
           paints above every segment and takes no pointer events, so the
           card's own tap target is unchanged. -->
      <div
        class="mbl-card-content"
        :class="`mbl-card-content--icons-${iconCount}`"
        data-cy="mobile-card-content"
      >
        <div
          v-if="variant !== 'week' && iconCount > 0"
          class="mbl-card-icons"
          data-cy="mobile-card-icons"
        >
          <UserCheck
            v-if="originalDetails?.created_by?.user_type === 'customer'"
            class="mbl-card-icon"
            role="img"
            aria-label="Scheduled by customer"
          />
          <Lock
            v-if="appointment.assignment_pinned"
            class="mbl-card-icon"
            role="img"
            aria-label="Pinned technician"
          />
          <CrownSimple
            v-if="appointment.customer?.membership"
            class="mbl-card-icon"
            role="img"
            aria-label="Membership customer"
          />
        </div>
        <div class="mbl-card-caption">
          <div class="mbl-card-title" data-cy="mobile-card-title">
            {{ appointment.service_category_name }}
          </div>
          <div
            v-if="variant !== 'week'"
            class="mbl-card-detail"
            data-cy="mobile-card-detail"
          >
            {{ detailLabel }}
          </div>
        </div>
      </div>
    </template>
    <!-- The four non-appointment markers (hold, available, unavailable,
         all-day). No appointment segments render here, and the appointment
         branch above renders no chip -- the two are mutually exclusive by
         construction (deriveMarkerKind). -->
    <div
      v-else
      class="mbl-card-marker"
      :class="markerClass"
      :style="markerStyle"
      data-cy="mobile-appointment-card-marker"
    >
      <div
        v-if="markerKind === 'hold'"
        class="mbl-card-chip"
        data-cy="mobile-card-hold-chip"
      >
        {{ holdLabel }}
      </div>
      <!-- Every calendar-event branch (available/unavailable/all_day) renders
           the event's own title through text interpolation and nothing else
           -- the name is tenant-authored free text, and interpolation is the
           control that keeps it inert (T-TB2253-10). In its own element, not
           bare in the marker: the marker is a flex box, and text-overflow
           never reaches a flex container's anonymous text item, so a title
           wider than the card was clipped mid-letter with no ellipsis. -->
      <span
        v-else
        class="mbl-card-marker-title"
        data-cy="mobile-card-marker-title"
        >{{ event.title }}</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
// The phone appointment card. Its narrow "week" variant (title only,
// truncated, no detail line, no icons) is specified and unit-tested ahead of
// a surface that can show it: no week view exists in this scheduler today,
// so nothing in production passes variant="week" yet and no Playwright spec
// fakes that state.
import { computed } from "vue";
import { customDateFormatWithTimezone } from "@/lib/helpers";

import CrownSimple from "@/images/CrownSimple.svg?component";
import Lock from "@/images/lock.svg?component";
import UserCheck from "@/images/UserCheck.svg?component";
import {
  boxSpanFromEvent,
  CARD_TIME_SEPARATOR,
  CardMarkerKind,
  CardVariant,
  ChainSegment,
  deriveChainSegments,
  deriveMarkerKind,
  formatCompactTimeRange
} from "@/SchedulerV2/mobileAppointmentCard";
import {
  PHONE_MARKER_COLORS,
  phoneMarkerHatch,
  useScheduleColors
} from "@/SchedulerV2/useScheduleColors";
import { AppointmentEvent, EventAPIResponse } from "@/typings/scheduler";

const { getPhoneWorkStatusKey, getPhoneFillColor, getPhoneTextColor } =
  useScheduleColors();

const props = withDefaults(
  defineProps<{
    event: AppointmentEvent;
    variant?: CardVariant;
  }>(),
  { variant: "day" }
);

const originalDetails = computed<EventAPIResponse | undefined>(
  () => props.event.original?.details
);
const appointment = computed(() => originalDetails.value?.appointment);
// Which ROW this is, matching what the desktop card does with the same two
// fields. `appointment.ghost` is set by the serializer on the REAL row when a
// placeholder exists for it; useEventCalendar then pushes a CLONE carrying
// `is_ghost: true, ghost: false` and keeps the real row untouched. So both
// rows reach this component, and only the clone is the placeholder.
//
// Reading `is_ghost || appointment.ghost` here routed BOTH to the hold branch,
// which drew the real appointment as a bare grey chip with no title, time,
// address, icons or chain. AppointmentEvent.vue keys its own "Hold" label on
// `is_ghost` alone for exactly this reason.
const isHoldPlaceholder = computed(() =>
  Boolean(originalDetails.value?.is_ghost)
);

// Colour, though, takes both -- the real row of a held appointment is tinted
// as one on the desktop board too (AppointmentEvent.vue's getEventStyles call
// passes the same disjunction).
const ghostTinted = computed(
  () =>
    Boolean(originalDetails.value?.is_ghost) ||
    Boolean(appointment.value?.ghost)
);

// D-08: which of the five branches this card renders. Gates the template
// switch instead of `appointment` truthiness alone, because a ghost payload
// (hold) DOES carry a real appointment sub-object -- deriveMarkerKind's own
// ghost-first ordering is what routes it to the hold branch instead of the
// ordinary appointment one.
const markerKind = computed<CardMarkerKind>(() =>
  deriveMarkerKind(originalDetails.value, isHoldPlaceholder.value)
);

// The same `timeline-event-{id}` identifying hook the desktop card's own
// two branches already carry (AppointmentEvent.vue lines 6 and 95) -- for
// the appointment branch, the scheduler_event's own id; for a calendar
// event, event_details.id. Pre-existing test/CSS code that locates a card
// by this class (scheduler_technician_control_row.spec.ts, unmodified by
// this plan) otherwise finds nothing once mobile_web_optimization renders
// this card instead of AppointmentEvent.vue's.
const timelineEventIdClass = computed(() => {
  // A hold payload is an appointment payload: its event_details is null, so
  // reading that alone gave every hold card the same `timeline-event-undefined`.
  const id =
    markerKind.value === "appointment"
      ? originalDetails.value?.id
      : (originalDetails.value?.event_details?.id ?? originalDetails.value?.id);
  return `timeline-event-${id}`;
});

const MARKER_CLASS: Record<Exclude<CardMarkerKind, "appointment">, string> = {
  hold: "mbl-card--hold",
  available: "mbl-card--available",
  unavailable: "mbl-card--unavailable",
  all_day: "mbl-card--all-day"
};

const markerClass = computed(() => {
  const kind = markerKind.value;
  return kind === "appointment" ? "" : MARKER_CLASS[kind];
});

// Bound as ordinary inline style properties, not CSS custom properties --
// the same precedent D-07's chevron fix established (a dynamic :style
// object key reaches a getComponentHtml-rendered element reliably only for
// plain style properties, never for a custom property read back via var()).
const markerStyle = computed<Record<string, string>>(() => {
  switch (markerKind.value) {
    case "hold":
      return {
        backgroundColor: PHONE_MARKER_COLORS.holdFill,
        border: `2px dashed ${PHONE_MARKER_COLORS.holdBorder}`,
        color: PHONE_MARKER_COLORS.holdText
      };
    case "available":
      // V1's own .schedule-event.available treatment
      // (calendar.scss:336-350): a white fill with a 2px dashed border in
      // V1's event-border grey, copied so the phone board and the V1 board
      // paint a calendar event the same way. No diagonal pattern any more:
      // an exclusive event's grey hatch was indistinguishable from the
      // travel band at a glance (G-TB-2253-5c).
      return {
        backgroundColor: PHONE_MARKER_COLORS.white,
        border: `2px dashed ${PHONE_MARKER_COLORS.eventBorder}`,
        // The three calendar-event branches (available/unavailable/all_day)
        // are the only marker kinds with no explicit label colour; without
        // it, {{ event.title }} inherits Mobiscroll's own ambient event text
        // colour instead of a colour this card chose (G-TB-2253-1c).
        color: PHONE_MARKER_COLORS.ink
      };
    case "unavailable":
      // V1's own .schedule-event treatment (calendar.scss:336-350): a flat
      // solid fill with a 1px solid border in V1's event-border grey,
      // copied so the phone board and the V1 board paint a calendar event
      // the same way. No diagonal pattern any more: this kind's grey hatch
      // was indistinguishable from the travel band at a glance
      // (G-TB-2253-5c).
      return {
        backgroundColor: PHONE_MARKER_COLORS.eventFill,
        border: `1px solid ${PHONE_MARKER_COLORS.eventBorder}`,
        color: PHONE_MARKER_COLORS.ink
      };
    case "all_day":
      // AC-06/D-08: the one calendar-event kind that keeps a hatch, in its
      // own pale-blue pair -- deliberately far in brightness from the grey
      // travel stripe, so a day-wide block never reads as travel time. The
      // ticket owner chose to keep this hatch when the other event kinds
      // moved to V1's flat fill. The width TREATMENT (own all-day row) is
      // the separate rule in MobileTimeline.vue keyed on the
      // mbl-card--all-day class this branch also carries.
      return {
        backgroundImage: phoneMarkerHatch("all_day"),
        color: PHONE_MARKER_COLORS.ink
      };
    default:
      return {};
  }
});

const statusKey = computed(() =>
  getPhoneWorkStatusKey(appointment.value, ghostTinted.value)
);
const fill = computed(() => getPhoneFillColor(statusKey.value));
const textColor = computed(() =>
  getPhoneTextColor(fill.value, statusKey.value)
);

// AC-01's "time range · street" line. The range is the SCHEDULED window
// (arrival_window_start_time/end_time) on every status, not the desktop
// card's per-status time source (running shows actuals, completed shows
// nothing) -- a dispatcher scanning a phone column is reading what was
// promised, not what happened. The chain segments in plan 02 draw "what
// happened".
// How many indicator icons the pill will hold. The title
// reserves exactly that much right padding instead of running underneath an
// absolutely-positioned pill whose width it cannot otherwise know. A class,
// not a custom property -- a dynamic :style custom property does not
// reliably reach a getComponentHtml-rendered element (see segmentStyle).
const iconCount = computed(() => {
  if (props.variant === "week") return 0;
  return [
    originalDetails.value?.created_by?.user_type === "customer",
    Boolean(appointment.value?.assignment_pinned),
    Boolean(appointment.value?.customer?.membership)
  ].filter(Boolean).length;
});

// The same label the desktop card shows for a hold: the word plus the time the
// placeholder is holding. AppointmentEvent.vue builds it from
// arrival_window_start_time in `h:mm`, so the two boards read alike -- a
// dispatcher moving between them should not have to learn two wordings.
// Falls back to the bare word when the payload carries no window.
// The week variant drops the time, for the reason it drops the detail line: the
// grid's vertical axis is the time. It also could not carry it -- the chip is
// anchored to the card's right edge, so in a 36px column the 53px "Hold 2:00"
// hung 23px off the LEFT edge and the card's overflow clipped the word, leaving
// a bare "2:00". A hold that reads as a time is worse than one that reads as a
// hold.
const holdLabel = computed(() => {
  if (props.variant === "week") return "Hold";
  const at = appointment.value?.arrival_window_start_time;
  if (!at) return "Hold";
  const time = customDateFormatWithTimezone(at, "h:mm");
  return time ? `Hold ${time}` : "Hold";
});

const detailLabel = computed(() => {
  const range = formatCompactTimeRange(
    appointment.value?.arrival_window_start_time,
    appointment.value?.arrival_window_end_time
  );
  const street = appointment.value?.address?.street;
  // The separator joins two present halves. An appointment missing either
  // window bound yields an empty range, which without this guard renders a
  // detail line that opens on a bare separator.
  if (!street) return range;
  return range ? `${range}${CARD_TIME_SEPARATOR}${street}` : street;
});

// The box boxSpanFromEvent returns -- the clamped Date pair, never the two
// display strings beside them. Null when event.date is not finite; every
// caller below treats a null box as "draw one full-height block", which is
// today's (plan 01's) behaviour and is what a spec fixture with no
// startDate/endDate/date gets.
const box = computed(() => boxSpanFromEvent(props.event));

// The style guide's three-part chain (D-03 as amended), or -- when there is
// no box to measure segments against -- the single full-height block plan
// 01 shipped. The 15-minute visual floor (MIN_VISUAL_MINUTES) is applied
// inside deriveChainSegments, against the box's own duration. The work
// segment's geometry comes entirely from the chain, floor included -- this
// computed never overwrites it.
const segments = computed<ChainSegment[]>(() => {
  if (!box.value || !appointment.value) {
    return [
      { kind: "work", topPct: 0, heightPct: 100, first: true, last: true }
    ];
  }
  return deriveChainSegments(
    appointment.value,
    box.value.boxStart,
    box.value.boxEnd,
    statusKey.value
  );
});

// The work segment renders FIRST in document order (see the template); this
// computed only controls iteration order, not which elements exist.
const orderedSegments = computed(() => {
  const work = segments.value.filter((s) => s.kind === "work");
  const leads = segments.value.filter((s) => s.kind !== "work");
  return [...work, ...leads];
});

// Pre-work is ALWAYS the accepted_pre_work colour (D-03), independent of
// whatever key the work block itself resolves to.
const preWorkFill = computed(() => getPhoneFillColor("accepted_pre_work"));
const preWorkTextColor = computed(() =>
  getPhoneTextColor(preWorkFill.value, "accepted_pre_work")
);

function segmentStyle(segment: ChainSegment): Record<string, string> {
  const geometry: Record<string, string> = {
    top: `${segment.topPct}%`,
    height: `${segment.heightPct}%`
  };
  if (segment.kind === "pre_work") {
    return {
      ...geometry,
      backgroundColor: preWorkFill.value,
      color: preWorkTextColor.value
    };
  }
  if (segment.kind === "travel" || segment.kind === "late") {
    // A gradient needs only these two paint properties -- no tile-repeat, no
    // tile-size, no solid fill underneath, since the gradient IS the whole
    // paint. Bound directly as ordinary inline style properties rather than
    // routed through a CSS custom property + var(): observed on a real
    // board, the custom-property route left a dynamic :style object key
    // unset on the live element even though sibling plain properties
    // (color, top, height) on the same object all applied.
    return {
      ...geometry,
      backgroundImage: phoneMarkerHatch(segment.kind),
      color: PHONE_MARKER_COLORS.markerText
    };
  }
  return {
    ...geometry,
    backgroundColor: fill.value,
    color: textColor.value
  };
}
</script>

<style lang="scss" scoped>
.mbl-card {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  // Named `card` so an element inside the work block can ask a question
  // about the CARD's height -- an unnamed container query only ever
  // reaches its nearest container, which for that element is the work
  // block. This is what the title spill rung below needs.
  container: card / size;
  // Belt and braces, not the mechanism: Mobiscroll's own
  // .mbsc-schedule-event wrapper is already `position: absolute; z-index:
  // 1`, so every card already sits in its own stacking context (measured --
  // a spilled title loses to a neighbouring card with or without this
  // declaration, once that wrapper is present). This covers the renders
  // that have no wrapper (a bare unit-test mount) and a future Mobiscroll
  // CSS change. What actually confines the spill on today's board is this
  // rule's own `overflow: hidden` plus that wrapper.
  isolation: isolate;
}

// Every segment (pre-work, travel, late, work) shares this positioning; the
// geometry itself (top/height percentages) is bound inline per segment in
// segmentStyle(), since it varies per appointment and per segment.
.mbl-card-segment {
  position: absolute;
  left: 0;
  right: 0;
  overflow: hidden;
}

// The lead segments sit above the work segment whenever their time spans
// overlap it -- the ordinary case once the work block is anchored on the
// arrival window (D-03 as amended) rather than on the segment above it. The
// work segment is emitted first in document order (see the template); this
// z-index is what keeps the mark a dispatcher reads the trip from from
// being painted over by the block underneath it.
.mbl-card-segment--pre_work,
.mbl-card-segment--travel,
.mbl-card-segment--late {
  z-index: 1;
}

.mbl-card-work {
  // Size containment only -- the name is gone with the spill rungs that were
  // the only thing that ever queried it. `container-type: size` still makes
  // this a stacking context, which is why it carries no z-index of its own:
  // pinning it at the base layer would trap its descendants below the lead
  // segments' own `z-index: 1`.
  container-type: size;
  padding: 2px 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

// Compound (0,2,0) on purpose: a container query may only style DESCENDANTS
// of its container, never the container itself, so this cannot be scoped to
// the spill placement. Written at the base rule's own specificity it would
// beat `.mbl-card-segment { overflow: hidden }` on source order alone, which
// a later reordering could silently reverse -- writing it compound removes
// that fragility. The one behavioural consequence is in the week variant
// (D-11), where the title shows at every height: a work block shorter than
// the title's own line can now let it bleed a pixel or two instead of
// clipping it. No week surface exists in production.
.mbl-card-segment.mbl-card-work {
  overflow: visible;
}

// Only the topmost segment (by deriveChainSegments' `first` flag) rounds its
// top corners, and only the bottommost (`last`) rounds its bottom ones --
// never both on every segment, which is what a plain border-radius on
// .mbl-card-work did before the chain existed.
.mbl-card-segment--rounded-top {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}

.mbl-card-segment--rounded-bottom {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}

// The travel and late marks draw the style guide's own diagonal-stripe
// gradient (phoneMarkerHatch), the same mechanism the all-day marker uses,
// at the same 135deg angle -- a scale-invariant gradient has no tile
// boundary to misalign the way the retired tiled SVG chevron did on a tall
// band. The exclusive and available calendar-event markers no longer share
// this mechanism (G-TB-2253-5c); they carry V1's flat fill instead.
//
// The gradient is bound inline per segment in segmentStyle(), not here: a
// CSS custom property set through a dynamic :style object key did not
// reliably reach the live element on a component rendered via
// getComponentHtml's mount-then-serialize path (the property came back
// unset on a real board, even though sibling style keys on the same object
// -- color, top, height -- all applied correctly). Ordinary style
// properties do not share that failure mode.

// Hidden below the title threshold: a 15px-floor card shows colour only.
// Thresholds are kept in sync by hand with CARD_TITLE_MIN_PX /
// CARD_DETAIL_MIN_PX in mobileAppointmentCard.ts, since a container query
// cannot read a TypeScript constant.
.mbl-card-title {
  display: none;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mbl-card-detail {
  display: none;
  font-size: 0.6875rem;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mbl-card-icons {
  display: none;
  position: absolute;
  top: 2px;
  right: 4px;
  gap: 2px;
  align-items: center;
}

.mbl-card-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

// In-block, this is a plain in-flow block: the work segment's single flex
// item, stretched to the segment's width by the parent's default
// `align-items: stretch`, with the title and detail line stacking inside it
// by normal flow -- nothing here changes the in-block rendering. The title
// spill rung below is the only thing that resets `position` and adds
// z-index, padding and colour; declare nothing else here for it to reset.
.mbl-card-content {
  position: absolute;
  inset: 0;
  // Above every segment (the leads carry z-index: 1) so the captions are
  // never painted over by a stripe they cross.
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2px 6px;
  // The card's own tap target must not change: this overlay covers the whole
  // card, so every event has to fall through it to the segments beneath.
  pointer-events: none;
}

// The four non-appointment marker branches (hold, available, unavailable,
// all-day) share this container: it is what the hold chip's own container
// query below measures, since these branches have no separate work-block
// sub-container the way the appointment branch does.
.mbl-card-marker {
  position: relative;
  width: 100%;
  height: 100%;
  container-type: size;
  box-sizing: border-box;
  padding: 2px 6px;
  display: flex;
  align-items: center;
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
}

// The title's own block, so it can ellipsise. text-overflow applies to a block
// container's inline content; a flex container's text is an anonymous flex
// item and the property is inert on it, which is why an ellipsis declared on
// .mbl-card-marker itself never showed and "Lunch" was clipped to "Lunc" in a
// week column. min-width: 0 lets a flex item shrink below its content width at
// all -- without it the span would overflow the marker instead of truncating.
.mbl-card-marker-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

// AC-05: the Hold chip. Dropped at the colour-only rung of the content
// ladder (the same CARD_TITLE_MIN_PX threshold the icons use below), rather
// than always shown, since a 15px-floor hold card has no room for it.
.mbl-card-chip {
  display: none;
  position: absolute;
  top: 2px;
  right: 4px;
  font-size: 0.625rem;
  font-weight: 700;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.08);
}

// CARD_TITLE_MIN_PX (30px), in-block: title and icons appear together,
// against the named `work` container.
@container card (min-height: 30px) {
  .mbl-card-title {
    display: block;
  }
  .mbl-card-icons {
    display: flex;
  }
}

// The Hold chip's own block, split out from the query above and kept
// UNNAMED on purpose: its nearest container is .mbl-card-marker, which
// carries no name, so a query naming `work` would stop matching it and the
// chip would vanish at every height. The browser resolves each selector
// against its own nearest containing ancestor at render time, which is why
// one shared threshold still serves two different containers even split
// into two blocks.
@container (min-height: 30px) {
  .mbl-card-chip {
    display: block;
  }
}

// In a week column the chip is anchored to a right edge that may be only 33px
// from the left one, so it is capped at the card's width and ellipsised at its
// END -- the one direction truncation is allowed to lose text from. The label
// is already the single word "Hold" there (holdLabel), so this only ever acts
// on a device font wide enough to break even that.
.mbl-card--week .mbl-card-chip {
  right: 2px;
  max-width: calc(100% - 4px);
  padding: 2px 3px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// CARD_DETAIL_MIN_PX (46px), in-block: the time-and-address line joins
// them, against the named `work` container.
@container card (min-height: 46px) {
  .mbl-card-detail {
    display: block;
  }
}

// The narrow week variant is a density read, not a short card: title only,
// truncated, no detail line and no icons regardless of the work block's
// actual height.
.mbl-card--week {
  .mbl-card-title {
    display: block;
  }
  .mbl-card-detail,
  .mbl-card-icons {
    display: none;
  }
}

// The caption treatment. The segment fill is status-derived and so unknowable at
// author time, so the text carries its own ground instead of relying on a
// brightness-picked ink that has to work over 12 different fills. Closed
// captions solve the same problem (arbitrary video behind arbitrary text)
// the same way.
//
// The ground hugs the glyphs rather than spanning the card, which is what makes
// it read as a caption rather than a banner.
// The ground sits on the BLOCK, shrunk to fit its text -- not on an inline
// span. Two inline grounds cannot be made to meet: half-leading leaves a gap,
// and closing it with vertical padding makes them overlap, which on a
// translucent ground composites 0.66 over 0.66 = 0.88 and draws a dark seam
// exactly where the join should be invisible. Consecutive blocks abut at
// zero, so the two captions stack into one continuous ribbon.
// ONE ground for both lines, sized to the wider of the two. Giving each line
// its own box cannot produce a clean corner: two boxes of different widths
// meet in a step, and rounding the step's corners is what read as an indent.
// A single box has four corners and no join, which is also how a real caption
// is drawn. The cost is that the shorter line carries some trailing ground --
// that is the shape being bought.
.mbl-card-caption {
  width: fit-content;
  max-width: 100%;
  padding: 0 4px;
  background: rgba(17, 17, 17, 0.66);
  color: #fff;
  border-radius: 3px;
}

.mbl-card-title,
.mbl-card-detail {
  max-width: 100%;
}

// The indicators get the same ground, as a pill. They sit over the fill with
// no text ladder to hide behind, so they need it more than the text does.
// `color: #fff` is what the icons inherit -- they draw with currentColor and
// bind no colour of their own.
// Matches the caption ribbons. The icons are single-colour
// (`fill="currentColor"`); the `fill="white"` in lock.svg and UserCheck.svg
// is on a <rect> inside <defs><clipPath>, which never renders, so there is
// no cutout to lose against a dark ground.
.mbl-card-icons {
  padding: 2px 4px;
  gap: 3px;
  background: rgba(17, 17, 17, 0.66);
  color: #fff;
  border-radius: 999px;
}

// Height drives, width follows the viewBox. The base rule squares all three
// at 12x12, which stretches lock.svg (a 10x11 viewBox) about 9% wide and
// squashes the 12x13 pair. Same specificity as the base rule, later in
// source order.
.mbl-card-icon {
  width: auto;
  height: 12px;
}

// Indicator clearance, only where the pill and the title can actually collide.
//
// The pill is pinned to the card's top; the text is centred in the card's
// full height. So they only meet on a SHORT card. Geometry, with the content
// block centred: the pill's bottom edge sits at 2px (content padding) + 2px
// (its own top) + 16px (12px icon + 2px padding each side) = 20px from the
// card's top. The title's top edge sits at (cardHeight - blockHeight) / 2,
// and the two-line block is 26.4px (13.2 + 13.2). They overlap while
// (H - 26.4) / 2 < 20, i.e. below H = 66.4px. 68 is the rounded-up floor.
//
// Above that the title takes the full width -- which is the common case, and
// what was truncating "Sewer Isolation ..." 30px early for no reason.
//
// The detail line never needs this: at its lowest rendering height (46px) its
// own top edge is already at 23px, below the pill.
@container card (max-height: 68px) {
  .mbl-card-content--icons-1 .mbl-card-caption {
    max-width: calc(100% - 24px);
  }

  .mbl-card-content--icons-2 .mbl-card-caption {
    max-width: calc(100% - 39px);
  }

  .mbl-card-content--icons-3 .mbl-card-caption {
    max-width: calc(100% - 54px);
  }
}
</style>
